import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {SendEmailRequestService} from '../../shared/services/email.service';
import {ReceiptDownloadService} from '../../shared/services/receipt-download.service';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {PacDetailComponent} from '../../components/pac-detail/pac-detail.component';
import {AdditionalPACData, PaymentCategory} from '../../models/additional-pac-data.model';
import {catchError, EMPTY, tap} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {PacService} from '../../shared/services/pac.service';
import {VirtualKeyboardComponent} from '../../components/virtual-keyboard/virtual-keyboard.component';
import {CustomModalComponent} from '../../components/custom-modal/custom-modal.component';
import {AppConstants} from '../../shared/app-constants';

@Component({
  selector: 'app-payment-success',
  standalone: true,
  imports: [CommonModule, FormsModule, PacDetailComponent, VirtualKeyboardComponent, CustomModalComponent],
  templateUrl: './payment-success.component.html',
  styleUrl: './payment-success.component.scss'
})
export class PaymentSuccessComponent implements OnInit {
  emailAddress: string | undefined = '';
  pacReference: string | undefined = '';
  pacDetails!: AdditionalPACData;
  isAborted: boolean = false;
  isPayNowTransactionMissing: boolean = false;
  allowDownloads: boolean = false;
  pacNumber: string | undefined = '';
  payNowAttemptedCount: number = 0;
  receiptActionTaken: boolean = false;
  today = new Date();

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly sendEmailRequestService: SendEmailRequestService,
    private readonly receiptDownloadService: ReceiptDownloadService,
    private readonly pacService: PacService
  ) {
  }

  @ViewChild('emailConfirmationModalRef') emailConfirmationModalRef!: CustomModalComponent;
  @ViewChild('leaveWithoutReceiptModalRef') leaveWithoutReceiptModalRef!: CustomModalComponent;
  confirmationMessage: string = 'Request to send receipt by email sent successfully.';
  leaveWithoutReceiptMessage: string = 'You haven’t generated or emailed the receipt yet. Do you still want to close the session?';


  ngOnInit(): void {
    this.loadData();
    const authDataRaw = localStorage.getItem('authData');
    if (authDataRaw) {
      const authData = JSON.parse(authDataRaw);
      this.allowDownloads = authData?.capabilities?.[0]?.allowDownloads === true;
    }
  }

  loadData() {
    const stateData = history.state?.pacDetails as AdditionalPACData | undefined;
    this.isAborted = history.state?.isAborted;
    this.isPayNowTransactionMissing = history.state?.isPayNowTransactionMissing;
    this.payNowAttemptedCount = history.state?.payNowAttemptedCount;
    this.emailAddress = stateData?.emailId;
    this.pacNumber = stateData?.pacNumber;
    this.fetchCourtDepositDetails(this.pacNumber || '');
    if (stateData) {
      this.handlePACData(stateData);
    } else {
      this.route.queryParams.subscribe(params => {
        const pacReference = params['pacReference'];
        if (pacReference) {
          this.fetchCourtDepositDetails(pacReference);
        }
      });
    }
  }

  handlePACData(data: AdditionalPACData) {
    const updatedData = {...data};
    if (updatedData.selectedAmountPayable != null) {
      updatedData.totalAmountPayable = updatedData.selectedAmountPayable;
      updatedData.balancePayable = updatedData.selectedAmountPayable - updatedData.amountPaid;
    }
    this.pacDetails = updatedData;
    if (this.pacDetails.paymentCategory === PaymentCategory.COURT_DEPOSIT) {
      this.fetchCourtDepositDetails(this.pacDetails?.pacNumber || '');
    }
  }

  isCourtPayment() {
    return this.pacDetails.paymentCategory === PaymentCategory.COURT_PAYMENT;
  }

  fetchCourtDepositDetails(pacNumber: string): void {
    // No PAC to look up (e.g. page opened without state/query params) - keep whatever we have instead of sending a blank PAC
    if (!pacNumber) return;
    this.pacService.fetchAdditionalInfo(pacNumber).pipe(
      tap(response => {
        this.pacDetails = response;
        if (this.pacDetails?.emailId) {
          this.emailAddress = this.pacDetails.emailId;
        }
        this.pacReference = this.pacDetails?.pacNumber;
        if (this.pacDetails.pacNumber?.startsWith(AppConstants.DEPOSIT_PAC_PREFIX)) {
          this.pacReference = this.pacDetails.depositDetailsInfo?.externalPacNumber;
        }
      }),
      catchError(error => {
        return EMPTY; // prevent further subscription
      }),
      finalize(() => {
      })
    ).subscribe();
  }

  onClick() {
    if (this.receiptActionTaken) {
      this.router.navigate(['']);
    } else {
      this.leaveWithoutReceiptModalRef.showModal();
    }
  }

  sendEmailReceipt() {
    this.receiptActionTaken = true;
    if (this.pacDetails.pacNumber && this.emailAddress) {
      this.sendEmailRequestService
        .sendEmail(this.pacDetails.pacNumber, this.emailAddress)
        .subscribe({
          next: () => {
            this.emailConfirmationModalRef.showModal();
          },
          error: (err) => {
          }
        });
    }
  }

  handleEmailConfirmationClose(confirmed: boolean) {
    this.emailConfirmationModalRef.hideModal();
  }

  handleLeaveWithoutReceiptClose(confirmed: boolean) {
    this.leaveWithoutReceiptModalRef.hideModal();
    if (confirmed) {
      this.router.navigate(['']);
    }
  }

  downloadReceipts() {
    this.receiptActionTaken = true;
    if (!this.pacDetails.pacNumber) {
      alert('PAC reference not found.');
      return;
    }
    const receiptList = Array.from(
      new Set(
        this.pacDetails?.paidLineItems
          ?.map(m => m.receiptNumber)
          .filter(receipt => receipt != null)
      )
    );
    const receiptCount = receiptList.length;

    if (this.allowDownloads) {
      if (receiptCount === 1) {
        this.receiptDownloadService.downloadReceipt(this.pacDetails.pacNumber).subscribe({
          next: ({body, headers}) => {
            const contentDisp = headers.get('Content-Disposition') || '';
            const fileName = contentDisp.split('filename=')[1]?.replaceAll(/"/g, '') || 'receipt.pdf'; // NOSONAR
            const url = URL.createObjectURL(body!); // NOSONAR
            Object.assign(document.createElement('a'), {href: url, download: fileName}).click();
            URL.revokeObjectURL(url);
          },
          error: () => alert('Failed to download receipt.')
        });
      } else {
        this.receiptDownloadService.downloadZip(this.pacDetails.pacNumber).subscribe({
          next: (blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'receipts.zip';
            a.click();
            URL.revokeObjectURL(url);
          },
          error: (err) => {
            alert('Failed to download receipts.');
          }
        });
      }
    }
  }

  hasTransactionReference(): boolean {
    return this.pacDetails.paidLineItems?.some(item => item.transactionId != null) ?? false;
  }

  isPayNowUnrecorded(): boolean {
    if (this.payNowAttemptedCount != undefined && this.pacDetails.payNowCompletedCount != null) {
      let payNowTransactionMissing = this.payNowAttemptedCount !== this.pacDetails.payNowCompletedCount;
      this.isPayNowTransactionMissing = payNowTransactionMissing;
      return payNowTransactionMissing;
    }
    return false;
  }

  showAmountFormatted(value: number | null | undefined): string {
    return AppConstants.showAmountFormatted(value);
  }

  getPaymentMode(mode: string): string {
    return AppConstants.getPaymentMode(mode);
  }
}
